/* Copyright (c) 2020-2022 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useForm } from "react-hook-form";
import { Button, theme } from "@nordcloud/gnui";
import { showSuccess } from "~/components";
import { useShowError } from "~/components/Error/useShowError";
import { SidebarTextField } from "~/components/SidebarForm/inputs/SidebarTextField";
import { SidebarForm } from "~/components/SidebarForm/SidebarForm";
import { SidebarFormButtons } from "~/components/SidebarForm/SidebarFormButtons";
import { ServiceGroup } from "~/models";
import {
  checkValue,
  checkVersion,
  generateActionSuccessText,
  useAxios,
} from "~/tools";

type Values = {
  version: string;
};

type AddNewVersionServiceGroupProps = {
  serviceGroup: ServiceGroup;
  refetch: () => void;
};

function AddNewVersionServiceGroup({
  serviceGroup,
  refetch,
}: AddNewVersionServiceGroupProps) {
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);

  const methods = useForm();

  const { showError } = useShowError();
  const { Axios } = useAxios();

  const onSubmit = async (values: Values) => {
    const flatServices = serviceGroup.services.map((s) => s.id);
    const organizeValues = {
      ...serviceGroup,
      version: checkValue(values.version),
      serviceIds: flatServices,
    };
    try {
      await Axios().post("/catalog/serviceGroups", organizeValues);
      showSuccess(
        generateActionSuccessText(`New Version of ${serviceGroup.name}`)()(
          "added"
        )()
      );
      refetch();
    } catch (e) {
      showError(e.response);
    } finally {
      setIsSidebarOpen(false);
    }
  };

  return (
    <>
      <Button
        onClick={() => setIsSidebarOpen(true)}
        icon="plus"
        severity="low"
        size="md"
        css={{ backgroundColor: theme.color.background.ui01 }}
      >
        Add New Version
      </Button>

      <SidebarForm
        title="Add new version"
        open={isSidebarOpen}
        setOpen={setIsSidebarOpen}
        formMethods={methods}
        submit={onSubmit}
      >
        <SidebarTextField
          name="version"
          label="Version"
          required
          registerOptions={{
            validate: checkVersion,
          }}
        />

        <SidebarFormButtons
          submitEnabled={!!methods.watch("version")}
          onCancel={() => setIsSidebarOpen(false)}
        />
      </SidebarForm>
    </>
  );
}

export { AddNewVersionServiceGroup };
